import {
    ChangeDetectionStrategy,
    Component,
    computed,
    EventEmitter,
    inject,
    input,
    OnDestroy,
    OnInit,
    Output,
    signal,
    Signal
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';

import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, Observable, of, timer } from 'rxjs';
import { ShPageHeaderComponent } from '../../../../elements/components/page-header/page-header.component';
import { Comparable } from '../../../../helpers/entities/comparable.interface';
import { Trackable } from '../../../../helpers/entities/trackable.interface';
import { ListLayoutComponent } from '../../../../layouts/list-layout/list-layout.component';
import { DisplayMode } from '../../../generic-overview/components/display-settings-bar/entities/display-mode.entity';
import {
    relevanceSortInfo,
    SortInfo
} from '../../../generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { ShGenericOverviewContainerComponent } from '../../../generic-overview/containers/generic-overview/generic-overview.container.component';

import { ActivatedRoute } from '@angular/router';
import { isEqual } from 'lodash-es';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    map,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    ActionGroup,
    ActionGroupModifier
} from '../../../../elements/components/action-group/entities/action-group.entity';
import { ActionItem } from '../../../../elements/components/action-item/entities/action-item.entity';
import { ShIconBadgeComponent } from '../../../../elements/components/icon-badge/icon-badge.component';
import { FormSelectOption } from '../../../../form-controls/entities/form-select-option.entity';
import { ActionMode } from '../../../generic-overview/components/context-action-bar/entities/select-mode.entity';
import { SortOption } from '../../../generic-overview/components/display-settings-bar/entities/sort-option.entity';
import { SortOptionToSortInfo } from '../../../generic-overview/components/display-settings-bar/helpers/sort-option-to-sort-info.helper';
import { PaginatedResults } from '../../../generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../../generic-overview/components/pagination/entities/pagination-info.entity';
import { ColumnDef } from '../../../list-view/entities/column-definition.entity';
import { SearchPageConfig } from '../../entities/search-page-config.entity';
import { Panel, PartialSearchState, SearchState } from '../../entities/search-page-state.entity';
import { SearchPageSandbox } from '../../search-page.sandbox';

@Component({
    standalone: true,
    selector: 'sh-search-page',
    templateUrl: './search-page.container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [],
    imports: [
        CommonModule,
        ShGenericOverviewContainerComponent,
        ShPageHeaderComponent,
        ListLayoutComponent,
        ShIconBadgeComponent
    ]
})
export class SearchPageContainerComponent<T extends Comparable & Trackable>
    extends BaseContainer
    implements OnInit, OnDestroy
{
    private readonly sandbox = inject(SearchPageSandbox);
    private readonly route = inject(ActivatedRoute);

    // #region Inputs
    public readonly $config = input.required<SearchPageConfig<T>>({ alias: 'config' });
    // #endregion

    // #region Outputs
    @Output() columnSelectionChanged = new EventEmitter<string[]>();
    // #endregion

    // #endregion

    // #region General variables
    public readonly $searchPageId = computed(() => this.$config().id);
    public globalActionItems$: Observable<ActionItem[]>;
    // #endregion

    // #region Search variables
    private readonly AUTO_REFRESH_TIME: number = 5000;

    private readonly $searchState = computed(() =>
        this.setSearchDefaults(this.$config(), this.sandbox.getSearchState()())
    );
    public readonly $fieldSearchOptions = computed(() => this.$config().search.fieldSearchOptions);

    private readonly searchState$: Observable<SearchState>;
    public searchBarActionItems$: Observable<ActionItem[]>;
    public searchPageResults$: Observable<PaginatedResults<T>>;

    public errorMessage$: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
    // #endregion

    // #region Selected items variables
    private readonly actionMode$ = new BehaviorSubject<ActionMode>(ActionMode.BULK);
    public readonly selectedItems$ = new BehaviorSubject<T[]>([]);
    public readonly $activeItem = signal<T | undefined>(undefined);
    public initialBatchSelect: boolean;
    // #endregion

    // #region Action variables
    public actions$!: Observable<ActionGroup[]>;
    public contextActions$: Observable<ActionGroup>;
    // #endregion

    // #region Columns variables
    public columns$: Observable<ColumnDef<any, T>[]>;
    public columnOptions$: Observable<FormSelectOption<string>[]>;
    // #endregion

    // #region Sort variables
    public readonly $sort = computed(() => this.$searchState().sortInfo);
    public readonly $sortOptions = computed(() => this.$config().sort.options);
    public readonly $allowSort = computed(() => this.$config().features.Sort && this.$sortOptions().length > 1);
    // #endregion

    // #region View variables
    public readonly $view = computed(() => this.$searchState().view);
    public readonly $viewOptions = computed(() => this.calculateViewOptions(this.$config()));
    public readonly $showViewToggle = computed(() => this.areOtherViewOptionsEnabled(this.$config()));
    // #endregion

    // #region Pagination variables
    public readonly $pageSizeOptions = computed(() => this.$config().pageSize.options);
    // #endregion

    // #region Filter variables
    public readonly $filterCount = computed(() => this.countSelectedFilters(this.$searchState()));
    // #endregion

    // #region Map variables
    private readonly MAP_MAX_RESULTS: number = 1000;
    private readonly MAP_SORT: SortInfo = relevanceSortInfo;
    // #endregion

    // #region Feature variables
    public readonly $imageFitModeSetting = this.sandbox.$imageFitSetting;
    public readonly $searchBarMode = computed(() => this.$config().search.searchBarMode);
    public readonly $allowSelection = computed(() => this.$config().features.Selection);
    public readonly $allowItemClick = computed(() => this.$config().features.RowClickAction);
    public readonly $allowBatchSelection = computed(() => this.$config().features.BatchSelection);
    public readonly $autoRefresh = computed(() => this.$config().features.AutoRefresh);
    // #endregion

    // #region Panel variables
    private $panelStates: Signal<Record<Panel, boolean>>;

    public readonly $hasFiltersPanel = computed(() => this.$config().features.filters);
    public readonly $hasHierarchyPanel = computed(() => this.$config().features.hierarchy);
    public readonly $hasDetailPanel = computed(() => this.$config().features.detail);

    public readonly $filterPanelOpen = computed(() => this.$panelStates().filters);
    public readonly $hierarchyPanelOpen = computed(() => this.$panelStates().hierarchy);
    public readonly $detailPanelOpen = computed(() => this.$panelStates().detail);
    // #endregion

    // #region Translation variables
    public readonly $noItemsSelectedLabelKey = computed(
        () => this.$config().translations?.itemsSelectedLabelKey ?? 'general.none-selected.generic'
    );
    public readonly $itemsSelectedLabelKey = computed(
        () => this.$config().translations?.itemsSelectedLabelKey ?? 'general.xx-selected.generic'
    );
    public readonly $xxResultsLabelKey = computed(
        () => this.$config().translations?.resultsLabelKey ?? 'general.xx-results.generic'
    );
    public readonly $noResultsLabelKey = computed(
        () => this.$config().translations?.noResultsLabelKey ?? 'general.no-results.generic'
    );
    // #endregion

    // #region Lifecycle
    constructor() {
        super();
        this.searchState$ = toObservable(this.$searchState).pipe(distinctUntilChanged(isEqual), shareReplay(1));
    }

    ngOnInit(): void {
        this.initPanelStates();
        this.initPageStateListener();
        this.initPageState();
        this.initColumns();
        this.initSearchPageResultsStream();
        this.initSearchBarActionsStream();
        this.initActionStreams();
        this.initSelection();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        // Save state
        this.sandbox.saveUrlState(this.$searchPageId());
        // Close Detail panel
        this.sandbox.savePanelState(this.$searchPageId(), Panel.DETAIL, false);
    }
    // #endregion

    // #region Page state
    private initPageState(): void {
        const hasQuery = this.hasUrlQueryParams();

        if (!hasQuery) {
            this.sandbox.restoreStoredUrlState(this.$searchPageId());
        }
    }

    private hasUrlQueryParams(): boolean {
        return Object.keys(this.route.snapshot.queryParams).length > 0;
    }

    private setSearchDefaults(config: SearchPageConfig<T>, searchState: PartialSearchState): SearchState {
        const pageSize = this.pageSizeSanitization(
            config.pageSize.options,
            config.pageSize.default,
            searchState.pageSize
        );

        const sortInfo = this.sortInfoSanitization(config.sort.options, config.sort.default, searchState.sortInfo);

        const view = this.viewSanitization(searchState.view);

        return {
            ...searchState,
            pageSize,
            sortInfo,
            view
        };
    }

    private initPageStateListener(): void {
        // Used to unset ActiveItem when detailPanel closes
        // Could be improved by just also setting/unsetting ActiveItem (and QueryIterators) in SearchPageStateService
        this.sandbox
            .getPanelsStateStream(this.$searchPageId())
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(panelState => {
                if (!panelState?.detail) {
                    this.$activeItem.set(undefined);
                }
            });
    }
    // #endregion

    // #region Columns
    private initColumns(): void {
        this.columns$ = this.$config().list.columns$;
        this.columnOptions$ = this.$config().list.columnOptions$;
    }
    // #endregion

    // #region Data refresh triggers
    private getLoadRefreshTriggers(): Observable<boolean[]> {
        const queryChanges$ = this.getQueryRefreshTrigger();

        const pageChange$ = this.searchState$.pipe(
            map(searchState => searchState.page),
            distinctUntilChanged(),
            map(() => true)
        );

        const pageSizeChange$ = this.searchState$.pipe(
            map(searchState => searchState.pageSize),
            distinctUntilChanged(),
            map(() => true)
        );

        const sortChange$ = this.searchState$.pipe(
            map(searchState => searchState.sortInfo),
            distinctUntilChanged(isEqual),
            map(() => true)
        );

        const mapToggled$ = this.searchState$.pipe(
            map(searchState => searchState.view === DisplayMode.MAP),
            distinctUntilChanged(),
            map(() => true)
        );

        const externalRefreshTriggers$ = this.getExternalRefreshTriggers();

        return combineLatest([
            queryChanges$,
            pageChange$,
            pageSizeChange$,
            sortChange$,
            mapToggled$,
            ...externalRefreshTriggers$
        ]);
    }

    private getQueryRefreshTrigger(): Observable<boolean> {
        return this.searchState$.pipe(
            map(searchState => searchState.structuredQuery),
            distinctUntilChanged(isEqual),
            map(() => true)
        );
    }

    private getAutoRefreshTrigger(): Observable<number> {
        return this.$autoRefresh() ? timer(0, this.AUTO_REFRESH_TIME) : of(0);
    }

    private getExternalRefreshTriggers(): Observable<boolean>[] {
        return this.$config().search.refreshTriggers.map(trigger => trigger.pipe(startWith(true)));
    }
    // #endregion

    // #region Search results
    private initSearchPageResultsStream(): void {
        const autoRefresh$ = this.getAutoRefreshTrigger();

        // Changes that need to show loading message
        const loadStateRefreshTriggers$ = this.getLoadRefreshTriggers().pipe(tap(this.onSearchStart.bind(this)));

        // Changes that do not need to show loading message
        const backgroundRefresh$ = combineLatest([autoRefresh$]);

        this.searchPageResults$ = combineLatest([loadStateRefreshTriggers$, backgroundRefresh$]).pipe(
            debounceTime(10),
            switchMap(_ =>
                this.getSearchPageResults(this.$searchState()).pipe(catchError(this.onSearchError.bind(this)))
            ),
            tap(this.onSearchEnd.bind(this)),
            takeUntil(this.destroy$),
            shareReplay(1)
        );
    }

    private getSearchPageResults(searchState: SearchState): Observable<PaginatedResults<T>> {
        const updatedSearchState: SearchState = this.setMapSearchFixedSearchOptions(searchState);

        return this.$config().search.getSearchPageResultsFct(updatedSearchState);
    }

    private setMapSearchFixedSearchOptions(searchState: SearchState): SearchState {
        const isMapShown: boolean = searchState.view === DisplayMode.MAP;

        const pageSize: number = isMapShown ? this.MAP_MAX_RESULTS : searchState.pageSize;
        const sortInfo: SortInfo = isMapShown ? this.MAP_SORT : searchState.sortInfo;

        return {
            ...searchState,
            pageSize,
            sortInfo
        };
    }

    private onSearchStart(): void {
        this.loading$.next(true);
        this.error$.next(false);
    }

    private onSearchEnd(): void {
        this.loading$.next(false);
    }

    private onSearchError(error: Error): Observable<PaginatedResults<T>> {
        const emptyResult: PaginatedResults<T> = {
            paginationInfo: {
                page: 0,
                resultsPerPage: this.$searchState().page,
                totalNumberOfResults: 0
            },
            results: []
        };

        this.error$.next(true);

        console.error('Something went wrong with the search operation', error.message);

        const customErrorFct = this.$config().search.getCustomErrorMsgFct;

        if (customErrorFct) {
            const errorMsg: string | undefined = customErrorFct(error);
            if (errorMsg) {
                this.errorMessage$.next(errorMsg);
            }
        }

        return of(emptyResult);
    }

    private initSearchBarActionsStream(): void {
        this.searchBarActionItems$ = this.$config().search.getSearchBarActionsFct();
    }
    // #endregion

    // #region Actions
    private initActionStreams(): void {
        this.globalActionItems$ = this.$config().actions.getGlobalActionsFct();

        this.actions$ = this.searchPageResults$.pipe(
            switchMap(paginatedResults => this.$config().actions.getActionsFct(paginatedResults.results)),
            takeUntil(this.destroy$)
        );

        this.contextActions$ = combineLatest([this.selectedItems$, this.actionMode$]).pipe(
            switchMap(([items, actionMode]) => this.getContextActions(items, actionMode)),
            map(actions => ({
                actions,
                modifiers: [ActionGroupModifier.Condense]
            })),
            takeUntil(this.destroy$),
            shareReplay(1)
        );
    }

    private getContextActions(items: T[], actionMode: ActionMode): Observable<ActionItem[]> {
        const batchActionFunction = this.$config().actions.getBatchActionsFct;
        if (actionMode === ActionMode.BATCH && batchActionFunction) {
            return batchActionFunction();
        }

        return this.$config().actions.getBulkActionsFct(items);
    }

    // #endregion

    // #region Selected items
    protected initSelection(): void {
        // Constantly verifies if selected items are still part of the search query
        // Any missing items will be automatically removed from the selection this way

        combineLatest([
            this.getQueryRefreshTrigger(),
            this.getAutoRefreshTrigger(),
            ...this.getExternalRefreshTriggers()
        ])
            .pipe(
                debounceTime(10),
                switchMap(_ => {
                    const storedIds = this.sandbox.getStoredSelected(this.$searchPageId());
                    return this.$config().search.getSelectedItemsFct(storedIds, this.$searchState());
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(initialSelectedItems => this.selectedItems$.next(initialSelectedItems));

        const initialActionMode = this.sandbox.getStoredActionMode(this.$searchPageId());
        this.initialBatchSelect = initialActionMode === ActionMode.BATCH;
        this.actionMode$.next(initialActionMode);
    }

    public onSelectedItemsChanged(items: T[]): void {
        const itemIds: string[] = items.map(item => item.trackBy());
        this.sandbox.saveSelectedState(this.$searchPageId(), itemIds);
        this.selectedItems$.next(items);
    }

    public onActionModeChanged(actionMode: ActionMode): void {
        this.actionMode$.next(actionMode);
        this.sandbox.saveActionModeState(this.$searchPageId(), actionMode);
    }
    // #endregion

    // #region Pagination
    private pageSizeSanitization(
        pageSizeOptions: number[],
        defaultPageSize: number,
        statePageSize: number | undefined
    ): number {
        if (statePageSize && pageSizeOptions.includes(statePageSize)) {
            return statePageSize;
        }

        return defaultPageSize;
    }

    public onPaginationInfoChanged(paginationInfo: PaginationInfo): void {
        this.sandbox.savePaginationState(paginationInfo);
    }
    // #endregion

    // #region Sorting
    private sortInfoSanitization(
        sortOptions: SortOption[],
        defaultSort: SortInfo,
        stateSort: SortInfo | undefined
    ): SortInfo {
        const sortInfo = stateSort ?? defaultSort;

        return sortOptions.find(option => isEqual(sortInfo, SortOptionToSortInfo(option))) ? sortInfo : defaultSort;
    }

    public onSortInfoChanged(sort: SortInfo): void {
        this.sandbox.saveSortState(sort);
    }
    // #endregion

    // #region View
    private viewSanitization(stateView: DisplayMode | undefined): DisplayMode {
        const view = stateView ?? DisplayMode.LIST;

        // Ensure an allowed view option is set
        return this.$viewOptions().includes(view) ? view : DisplayMode.LIST;
    }

    private calculateViewOptions(config: SearchPageConfig<T>): DisplayMode[] {
        const viewOptions: DisplayMode[] = [DisplayMode.LIST];

        if (config.features.GridView) {
            viewOptions.push(DisplayMode.GRID);
        }

        if (config.features.MapView) {
            viewOptions.push(DisplayMode.MAP);
        }

        return viewOptions;
    }

    private areOtherViewOptionsEnabled(config: SearchPageConfig<T>) {
        return config.features.GridView || config.features.MapView;
    }

    public onViewChanged(view: DisplayMode): void {
        this.sandbox.saveViewState(view);
    }
    // #endregion

    // #region Panels
    private initPanelStates(): void {
        this.$panelStates = this.sandbox.getPanelsState(this.$searchPageId());
    }

    private countSelectedFilters(searchState: SearchState): number {
        return searchState.structuredQuery.Facets.flatMap(facet => facet.Buckets).length;
    }

    public toggleFilterPanel(open: boolean): void {
        this.sandbox.savePanelState(this.$searchPageId(), Panel.FILTERS, !open);
    }

    public toggleHierarchyPanel(open: boolean): void {
        this.sandbox.savePanelState(this.$searchPageId(), Panel.HIERARCHY, !open);
    }
    // #endregion

    // #region Events
    public onRowClicked(item: T): void {
        if (this.$hasDetailPanel()) {
            this.$activeItem.set(item);
            this.sandbox.savePanelState(this.$searchPageId(), Panel.DETAIL, true);
        }

        const onRowClickFct = this.$config().actions.onRowClickFct;
        if (onRowClickFct) {
            onRowClickFct(item);
        }
    }

    public onThumbnailClicked(item: T): void {
        if (!this.$config().features.PreviewOnThumbnailClick) {
            this.onRowClicked(item);
            return;
        }

        const onThumbnailClickedFct = this.$config().actions.onThumbnailClickedFct;
        if (onThumbnailClickedFct) {
            onThumbnailClickedFct(item);
        }
    }

    public onColumnSelectionChanged(selectedColumns: string[]): void {
        this.columnSelectionChanged.emit(selectedColumns);
    }
    // #endregion
}
